import React, { useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { LOCALES, AppLocale } from "src/i18n/localesConfig";
import { useLocale } from "src/i18n/LocaleProvider";
import { switchLocalePath } from "src/i18n/paths";

type LanguageSwitcherProps = {
  /** Show only the globe icon on extra-small screens. */
  compact?: boolean;
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const { locale, isAppRoute, setAppLocale } = useLocale();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSelect = (next: AppLocale) => {
    setAnchor(null);
    if (next === locale) return;
    if (isAppRoute) {
      setAppLocale(next);
      return;
    }
    navigate(switchLocalePath(location.pathname, next));
  };

  return (
    <>
      <Button
        startIcon={<LanguageIcon fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={t("nav.language")}
        sx={{
          color: "#cbd5e1",
          textTransform: "none",
          fontSize: "0.8rem",
          borderRadius: 999,
          px: 1.5,
          minWidth: 0,
          "&:hover": { bgcolor: "rgba(30, 41, 59, 0.5)", color: "#fff" },
          ...(compact && {
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } },
          }),
        }}
      >
        <Box
          component="span"
          sx={compact ? { display: { xs: "none", sm: "inline" } } : undefined}
        >
          {LOCALES[locale].nativeLabel}
        </Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: "#0f172a",
            border: "1px solid rgba(30, 41, 59, 1)",
            color: "#e2e8f0",
          },
        }}
      >
        {(Object.keys(LOCALES) as AppLocale[]).map((code) => (
          <MenuItem
            key={code}
            selected={code === locale}
            onClick={() => handleSelect(code)}
          >
            <ListItemText
              primary={LOCALES[code].nativeLabel}
              secondary={
                <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                  {LOCALES[code].label} · {LOCALES[code].currency}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
